import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EmbedLinkConfigService } from '../services/embed-link-config.service';

@Pipe({
  name: 'safeHtml'
})
export class SafeHtmlPipe implements PipeTransform {
  constructor(
    private sanitizer: DomSanitizer,
    private linkConfig: EmbedLinkConfigService,
  ) {}

  transform(value: string): SafeHtml {
    if (!value) return '';

    // Bước 0: Trích xuất tất cả code block và inline code ra khỏi chuỗi chính
    const codeSnippets: string[] = [];
    const placeholder = (index: number) => `__CODE_BLOCK_${index}__`;

    let text = value.replace(/\\n/g, '\n');

    // Code block: ```...```
    text = text.replace(/```([\s\S]*?)```/g, (match, p1) => {
      codeSnippets.push(`<pre><code>${p1}</code></pre>`);
      return placeholder(codeSnippets.length - 1);
    });

    // Inline code: `...`
    text = text.replace(/`([^`]+)`/g, (match, p1) => {
      codeSnippets.push(`<code style="font-weight: bold;">${p1}</code>`);
      return placeholder(codeSnippets.length - 1);
    });

    let processed = text;

    // Bước 1: Xử lý ảnh trước khi link
    processed = processed.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="rounded-xl zoom">');

    // Bước 2: Link
    const linkMatches = processed.match(/\[(.*?)\]\((.*?)\)/g) || [];
    for (const match of linkMatches) {
      if (!match.startsWith('!')) {
        const text = match.match(/\[(.*?)\]/)?.[1] ?? '';
        const url = match.match(/\((.*?)\)/)?.[1] ?? '';
        const target = this.linkConfig.resolveTarget(url);
        processed = processed.replace(match, `<a href="${url}" target="${target}">${text}</a>`);
      }
    }

    // Bước 3: Heading
    processed = processed.replace(/^\s*#{6}\s+(.+)$/gm, '<h6>$1</h6>');
    processed = processed.replace(/^\s*#{5}\s+(.+)$/gm, '<h5>$1</h5>');
    processed = processed.replace(/^\s*#{4}\s+(.+)$/gm, '<h4>$1</h4>');
    processed = processed.replace(/^\s*#{3}\s+(.+)$/gm, '<h3>$1</h3>');
    processed = processed.replace(/^\s*#{2}\s+(.+)$/gm, '<h2>$1</h2>');
    processed = processed.replace(/^\s*#{1}\s+(.+)$/gm, '<h1>$1</h1>');

    // Bước 4: List - đánh dấu unordered và ordered list items
    processed = processed.replace(/^\s*[-*]\s+(.+)$/gm, '<ul-item>$1</ul-item>');
    processed = processed.replace(/^\s*\d+\.\s+(.+)$/gm, '<ol-item>$1</ol-item>');

    // Bước 5: Formatting (bold, italic)
    processed = processed.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
    processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    processed = processed.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Bước 6: Xuống dòng
    processed = processed.replace(/\n/g, '<br>');

    // Bước 7: Table xử lý
    const tableRegex = /^\|(.+)\|$/g;
    const lines = processed.split('<br>');
    let tableHtml = '';
    let isTable = false;
    let tableRows: string[] = [];
    let finalProcessed = '';

    for (const line of lines) {
      if (line.match(tableRegex)) {
        if (line.match(/^[-|]{3,}$/)) continue;
        if (!isTable) {
          isTable = true;
          tableHtml = '<table border="1" style="border-collapse: collapse;">';
        }
        const cells = line
          .split('|')
          .slice(1, -1)
          .map(cell => `<td style="padding: 8px; border: 1px solid #ddd;">${cell.trim()}</td>`)
          .join('');
        tableRows.push(cells);
      } else {
        if (isTable) {
          tableHtml +=
            '<thead><tr>' +
            tableRows.map(row => `<tr>${row}</tr>`).join('') +
            '</tr></thead><tbody>';
          tableHtml += '</tbody></table>';
          finalProcessed += tableHtml;
          tableHtml = '';
          isTable = false;
          tableRows = [];
        }
        finalProcessed += line + '<br>';
      }
    }

    if (isTable) {
      tableHtml += tableRows.map(row => `<tr>${row}</tr>`).join('');
      tableHtml += '</tbody></table>';
      finalProcessed += tableHtml;
    }

    processed = finalProcessed;

    // Bước 8: Gắn lại các đoạn code đã bị tách ra
    processed = processed.replace(/__CODE_BLOCK_(\d+)__/g, (_, index) => codeSnippets[+index]);

    // Bước 9: Wrap list items trong <ul> và <ol> (xử lý từng dòng)
    const listLines = processed.split('<br>');
    let result = '';
    let inUlList = false;
    let inOlList = false;

    for (let i = 0; i < listLines.length; i++) {
      const line = listLines[i];
      const isUlItem = line.includes('<ul-item>');
      const isOlItem = line.includes('<ol-item>');

      if (isUlItem) {
        if (!inUlList) {
          result += '<ul style="list-style: none !important; list-style-type: none !important; padding-left: 1rem; margin: 0.5rem 0;">';
          inUlList = true;
        }
        result += line.replace(/<ul-item>/g, '<li style="list-style: none !important; list-style-type: none !important;">').replace(/<\/ul-item>/g, '</li>');
      } else if (isOlItem) {
        if (!inOlList) {
          result += '<ol style="list-style: none !important; list-style-type: none !important; padding-left: 1rem; margin: 0.5rem 0;">';
          inOlList = true;
        }
        result += line.replace(/<ol-item>/g, '<li style="list-style: none !important; list-style-type: none !important;">').replace(/<\/ol-item>/g, '</li>');
      } else {
        // Đóng list nếu đang mở
        if (inUlList) {
          result += '</ul>';
          inUlList = false;
        }
        if (inOlList) {
          result += '</ol>';
          inOlList = false;
        }
        result += line + '<br>';
      }
    }

    // Đóng list nếu còn mở ở cuối
    if (inUlList) result += '</ul>';
    if (inOlList) result += '</ol>';

    // Loại bỏ <br> thừa ở cuối
    processed = result.replace(/<br>$/, '');

    // Bước cuối: chuẩn hóa target cho MỌI thẻ <a> (kể cả HTML <a target="_blank">
    // dựng sẵn từ backend mà bước markdown ở trên không đụng tới). Dựa vào
    // EmbedLinkConfigService để link cùng origin trang cha mở bằng _top.
    processed = processed.replace(/<a\b([^>]*)>/gi, (full, attrs) => {
      const hrefMatch = attrs.match(/href\s*=\s*"([^"]*)"/i) || attrs.match(/href\s*=\s*'([^']*)'/i);
      if (!hrefMatch) return full;
      const href = hrefMatch[1].replace(/&amp;/g, '&');
      const target = this.linkConfig.resolveTarget(href);
      // Bỏ target cũ (nếu có) rồi gắn target đã tính.
      const attrsWithoutTarget = attrs.replace(/\s*target\s*=\s*("[^"]*"|'[^']*')/gi, '');
      return `<a${attrsWithoutTarget} target="${target}">`;
    });

    return this.sanitizer.bypassSecurityTrustHtml(processed);
  }
}
